import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, ArrowRight, Check, CheckSquare, Play, Route as RouteIcon, Search, Sparkles, Target, Trash2, UserPlus } from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from '../api'
import { Button, EmptyState, Status } from '../components'
import type { DriverUser } from '../types'

type RouteSummary = { id: string; name: string; status: string; driverId: string; driverName: string; plannedDistanceKm: number; plannedStopCount: number; deliveredCount: number; notFoundCount: number }
type BatchDetail = { id: string; name: string; status: string; plannedDeliveryDate?: string; depotName: string; depotAddress: string }
type BatchOrder = {
  id: string
  recipientName: string
  cep: string
  street: string
  streetNumber: string
  neighborhood: string
  city: string
  status: string
  geocodeStatus: string
  eligibility: string
  assignedDriverId?: string | null
}
type Assignments = Record<string, string[]>

function normalizeSearch(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR').trim()
}

export function BatchDetailPage() {
  const { id = '' } = useParams()
  const [batch, setBatch] = useState<BatchDetail | null>(null)
  const [routes, setRoutes] = useState<RouteSummary[]>([])
  const [drivers, setDrivers] = useState<DriverUser[]>([])
  const [orders, setOrders] = useState<BatchOrder[]>([])
  const [assignments, setAssignments] = useState<Assignments>({})
  const [activeDriverId, setActiveDriverId] = useState('')
  const [automaticDriverIds, setAutomaticDriverIds] = useState<string[]>([])
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [targetOrdersPerDriver, setTargetOrdersPerDriver] = useState(40)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const navigate = useNavigate()

  const load = async () => {
    const [detail, userResult, orderResult] = await Promise.all([
      api<{ batch: BatchDetail; routes: RouteSummary[] }>(`/api/batches/${id}`),
      api<{ users: DriverUser[] }>('/api/users'),
      api<{ orders: BatchOrder[] }>(`/api/batches/${id}/orders`),
    ])
    setBatch(detail.batch)
    setRoutes(detail.routes)
    setOrders(orderResult.orders)
    const available = userResult.users.filter((user) => user.role === 'DRIVER' && Number(user.isActive) === 1 && user.hasPhoto)
    setDrivers(available)
    setActiveDriverId((current) => current || available[0]?.id || '')
    setAutomaticDriverIds((current) => {
      const availableIds = new Set(available.map((driver) => driver.id))
      const preserved = current.filter((driverId) => availableIds.has(driverId))
      return preserved.length ? preserved : available.map((driver) => driver.id)
    })
    const persisted: Assignments = {}
    orderResult.orders.forEach((order) => {
      if (!order.assignedDriverId) return
      persisted[order.assignedDriverId] = [...(persisted[order.assignedDriverId] ?? []), order.id]
    })
    setAssignments(persisted)
  }
  useEffect(() => { void load() }, [id])

  const ownerByOrder = useMemo(() => {
    const owners = new Map<string, string>()
    Object.entries(assignments).forEach(([driverId, orderIds]) => orderIds.forEach((orderId) => owners.set(orderId, driverId)))
    return owners
  }, [assignments])

  const filteredOrders = useMemo(() => {
    const term = normalizeSearch(search)
    if (!term) return orders
    const tokens = term.split(/\s+/).filter(Boolean)
    const digits = search.replace(/\D/g, '')
    return orders.filter((order) => {
      const addressText = normalizeSearch(`${order.neighborhood} ${order.city} ${order.street} ${order.recipientName}`)
      return tokens.every((token) => addressText.includes(token)) || Boolean(digits && order.cep.replace(/\D/g, '').includes(digits))
    })
  }, [orders, search])

  const activeDriver = drivers.find((driver) => driver.id === activeDriverId)
  const activeAssigned = assignments[activeDriverId] ?? []
  const assignedTotal = ownerByOrder.size

  const toggleOrder = (orderId: string) => {
    setSelectedOrderIds((current) => current.includes(orderId) ? current.filter((id) => id !== orderId) : [...current, orderId])
  }
  const selectAllResults = () => {
    const visibleIds = filteredOrders.map((order) => order.id)
    setSelectedOrderIds((current) => [...new Set([...current, ...visibleIds])])
  }
  const selectToTarget = () => {
    if (!activeDriverId) return
    const remaining = Math.max(0, targetOrdersPerDriver - activeAssigned.length)
    const candidates = filteredOrders.filter((order) => !ownerByOrder.has(order.id)).slice(0, remaining).map((order) => order.id)
    setSelectedOrderIds((current) => [...new Set([...current, ...candidates])])
  }
  const assignSelected = () => {
    if (!activeDriverId || !selectedOrderIds.length) return
    setAssignments((current) => {
      const next = Object.fromEntries(Object.entries(current).map(([driverId, orderIds]) => [
        driverId,
        orderIds.filter((orderId) => !selectedOrderIds.includes(orderId)),
      ])) as Assignments
      next[activeDriverId] = [...new Set([...(next[activeDriverId] ?? []), ...selectedOrderIds])]
      return next
    })
    setSelectedOrderIds([])
  }
  const clearDriver = (driverId: string) => {
    setAssignments((current) => ({ ...current, [driverId]: [] }))
    setSelectedOrderIds([])
  }

  const toggleAutomaticDriver = (driverId: string) => {
    setAutomaticDriverIds((current) => current.includes(driverId) ? current.filter((id) => id !== driverId) : [...current, driverId])
  }

  const distributeAutomatically = async () => {
    setBusy(true); setMessage('')
    try {
      const result = await api<{ assignments: Array<{ driverId: string; orderIds: string[] }>; capacity: number }>(`/api/batches/${id}/suggest-assignments`, {
        method: 'POST',
        body: JSON.stringify({ driverIds: automaticDriverIds, targetOrdersPerDriver }),
      })
      const suggested = Object.fromEntries(result.assignments.map((assignment) => [assignment.driverId, assignment.orderIds])) as Assignments
      setAssignments(suggested)
      setSelectedOrderIds([])
      setMessage(`${orders.length} pedidos distribuídos automaticamente entre ${result.assignments.length} entregadores. Revise e ajuste antes de calcular as rotas.`)
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : 'Falha ao distribuir automaticamente')
    } finally { setBusy(false) }
  }

  const removeSelectedFromBatch = async () => {
    if (!selectedOrderIds.length || !window.confirm(`Retirar ${selectedOrderIds.length} pedido(s) desta remessa e devolver para a fila?`)) return
    setBusy(true); setMessage('')
    try {
      await api(`/api/batches/${id}/orders`, { method: 'DELETE', body: JSON.stringify({ orderIds: selectedOrderIds }) })
      setSelectedOrderIds([])
      setMessage('Pedidos retirados da remessa e devolvidos para a fila.')
      await load()
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : 'Falha ao retirar pedidos da remessa')
    } finally { setBusy(false) }
  }

  const cancelBatch = async () => {
    if (!window.confirm('Cancelar esta remessa? Todos os pedidos voltarão para a fila e nenhuma rota será liberada.')) return
    setBusy(true); setMessage('')
    try {
      await api(`/api/batches/${id}`, { method: 'DELETE' })
      navigate('/remessas')
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : 'Falha ao cancelar a remessa')
      setBusy(false)
    }
  }

  const plan = async () => {
    const payloadAssignments = drivers
      .map((driver) => ({ driverId: driver.id, orderIds: assignments[driver.id] ?? [] }))
      .filter((assignment) => assignment.orderIds.length)
    setBusy(true); setMessage('')
    try {
      const overloaded = payloadAssignments.find((assignment) => assignment.orderIds.length > targetOrdersPerDriver)
      if (overloaded) throw new ApiError(`A meta é de ${targetOrdersPerDriver} pedidos por entregador. Redistribua os pedidos que ultrapassaram esse limite.`, 409)
      const result = await api<{ routes: RouteSummary[]; unassignedCount: number }>(`/api/batches/${id}/plan`, {
        method: 'POST',
        body: JSON.stringify({ assignments: payloadAssignments, targetOrdersPerDriver }),
      })
      const assigned = payloadAssignments.reduce((sum, assignment) => sum + assignment.orderIds.length, 0)
      setMessage(`${result.routes.length} rotas calculadas com ${assigned} pedidos. ${result.unassignedCount} pedidos não selecionados voltarão para a fila quando as rotas forem liberadas.`)
      await load()
    } catch (caught) { setMessage(caught instanceof ApiError ? caught.message : 'Falha ao planejar') }
    finally { setBusy(false) }
  }
  const release = async () => {
    setBusy(true); setMessage('')
    try { await api(`/api/batches/${id}/release`, { method: 'POST' }); setMessage('Rotas liberadas para os entregadores. Os pedidos não atribuídos voltaram para a fila.'); await load() }
    catch (caught) { setMessage(caught instanceof ApiError ? caught.message : 'Falha ao liberar') }
    finally { setBusy(false) }
  }

  if (!batch) return <div className="page"><div className="page-skeleton">Carregando remessa...</div></div>
  const canPlan = ['DRAFT', 'PLANNING'].includes(batch.status)
  return (
    <div className="page">
      <Link to="/remessas" className="back-link"><ArrowLeft size={16} /> Voltar para remessas</Link>
      <header className="page-header"><div><p className="eyebrow">Remessa</p><h1>{batch.name}</h1><p>Saída e retorno: {batch.depotName} · {batch.depotAddress}</p></div><div className="batch-header-actions"><Status value={batch.status} />{canPlan ? <Button variant="danger" onClick={() => void cancelBatch()} loading={busy}><Trash2 size={15} /> Cancelar remessa</Button> : null}</div></header>
      {message ? <div className="notice">{message}</div> : null}

      {canPlan ? <section className="route-builder">
        <div className="section-title"><div><h2>Distribuir pedidos por entregador</h2><p>Busque um bairro ou CEP, selecione pedidos e atribua à pessoa responsável.</p></div><UserPlus size={19} /></div>
        <div className="route-builder-controls">
          <label className="field route-target"><span>Meta por entregador</span><div><Target size={15} /><input aria-label="Meta de pedidos por entregador" type="number" min={1} max={500} value={targetOrdersPerDriver} onChange={(event) => setTargetOrdersPerDriver(Math.max(1, Math.min(500, Number(event.target.value) || 1)))} /></div><small>Começa em 40 e pode ser ajustada.</small></label>
          <label className="route-search"><Search size={17} /><input aria-label="Buscar por bairro ou CEP" placeholder="Buscar bairro ou CEP, ex.: Taquara ou 22710" value={search} onChange={(event) => setSearch(event.target.value)} /></label>
        </div>
        <div className="automatic-distribution">
          <div><Sparkles size={18} /><span><strong>Distribuição automática</strong><small>Marque os entregadores participantes. O sistema agrupa por proximidade e respeita o máximo definido.</small></span></div>
          <div><span>{automaticDriverIds.length} entregadores · capacidade de {(automaticDriverIds.length * targetOrdersPerDriver).toLocaleString('pt-BR')}</span><Button variant="secondary" onClick={() => void distributeAutomatically()} loading={busy} disabled={!automaticDriverIds.length || !orders.length}><Sparkles size={15} /> Distribuir automaticamente</Button></div>
        </div>

        <div className="assignment-workspace">
          <aside className="driver-assignment-list" aria-label="Entregadores">
            <div className="assignment-column-title"><strong>Entregadores</strong><span>{assignedTotal} de {orders.length} pedidos atribuídos</span></div>
            {drivers.map((driver) => {
              const count = assignments[driver.id]?.length ?? 0
              const difference = targetOrdersPerDriver - count
              return <article key={driver.id} className={driver.id === activeDriverId ? 'active' : ''}>
                <label title="Incluir na distribuição automática"><input aria-label={`Incluir ${driver.fullName} na distribuição automática`} type="checkbox" checked={automaticDriverIds.includes(driver.id)} onChange={() => toggleAutomaticDriver(driver.id)} /></label>
                <button type="button" onClick={() => { setActiveDriverId(driver.id); setSelectedOrderIds([]) }}>
                  <span><strong>{driver.fullName}</strong><small>{count} pedidos · {difference >= 0 ? `${difference} até a meta` : `${Math.abs(difference)} acima da meta`}</small></span>
                  <b>{count}/{targetOrdersPerDriver}</b>
                </button>
              </article>
            })}
            {!drivers.length ? <EmptyState title="Cadastre um entregador" description="Crie os acessos da equipe antes de distribuir pedidos." /> : null}
          </aside>

          <div className="order-assignment-picker">
            <div className="assignment-column-title"><strong>{search ? `Resultados para “${search}”` : 'Pedidos da remessa'}</strong><span>{filteredOrders.length} encontrados · {selectedOrderIds.length} marcados</span></div>
            <div className="assignment-toolbar">
              <Button variant="secondary" onClick={selectToTarget} disabled={!activeDriverId || activeAssigned.length >= targetOrdersPerDriver}><Target size={14} /> Selecionar até a meta</Button>
              <Button variant="secondary" onClick={selectAllResults} disabled={!filteredOrders.length}><CheckSquare size={14} /> Selecionar todos</Button>
              <Button variant="ghost" onClick={() => setSelectedOrderIds([])} disabled={!selectedOrderIds.length}>Limpar seleção</Button>
              {batch.status === 'DRAFT' ? <Button variant="danger" onClick={() => void removeSelectedFromBatch()} disabled={!selectedOrderIds.length || busy}><Trash2 size={14} /> Retirar da remessa</Button> : null}
            </div>
            <div className="assignment-order-list">
              {filteredOrders.map((order) => {
                const ownerId = ownerByOrder.get(order.id)
                const owner = drivers.find((driver) => driver.id === ownerId)
                return <label key={order.id} className={ownerId ? 'assigned' : ''}>
                  <input type="checkbox" checked={selectedOrderIds.includes(order.id)} onChange={() => toggleOrder(order.id)} />
                  <span><strong>{order.neighborhood} · CEP {order.cep}</strong><small>{order.recipientName} · {order.street}, {order.streetNumber}</small></span>
                  {owner ? <em>{owner.fullName}</em> : <em>Livre</em>}
                  <Check size={15} />
                </label>
              })}
              {!filteredOrders.length ? <EmptyState title="Nenhum pedido encontrado" description="Tente outro bairro ou informe parte do CEP." /> : null}
            </div>
            <div className="assignment-action">
              <div><strong>{activeDriver?.fullName ?? 'Escolha um entregador'}</strong><span>{activeAssigned.length} pedidos já atribuídos · {selectedOrderIds.length} selecionados agora</span></div>
              <div><Button variant="ghost" onClick={() => activeDriverId && clearDriver(activeDriverId)} disabled={!activeAssigned.length}>Limpar entregador</Button><Button onClick={assignSelected} disabled={!activeDriverId || !selectedOrderIds.length}>Atribuir selecionados</Button></div>
            </div>
          </div>
        </div>
      </section> : null}

      {canPlan ? <div className="sticky-actions"><div><strong>{assignedTotal} pedidos em {Object.values(assignments).filter((orderIds) => orderIds.length).length} entregadores</strong><span>{orders.length - assignedTotal} não atribuídos voltarão para a fila quando a remessa for liberada.</span></div><Button onClick={() => void plan()} loading={busy} disabled={!assignedTotal}><RouteIcon size={16} /> Calcular rotas</Button></div> : null}
      <section className="routes-section"><div className="section-title"><div><h2>Rotas</h2><p>Abra cada rota para revisar a ordem e os endereços antes de liberar.</p></div>{batch.status === 'PLANNING' && routes.length ? <Button onClick={() => void release()} loading={busy}><Play size={16} /> Liberar para entrega</Button> : null}</div><div className="route-list">{routes.map((route) => <Link className="route-card" to={`/rotas/${route.id}`} key={route.id} aria-label={`Ver ${route.name} de ${route.driverName}`}><div className="route-index">{route.name.match(/\d+/)?.[0] ?? '—'}</div><div><strong>{route.driverName}</strong><span>{Number(route.plannedStopCount)} paradas · {Number(route.plannedDistanceKm).toFixed(1)} km · {Number(route.deliveredCount ?? 0)} entregues{Number(route.notFoundCount ?? 0) ? ` · ${route.notFoundCount} tentativas` : ''}</span></div><Status value={route.status} /><ArrowRight className="route-open-icon" size={17} /></Link>)}</div>{!routes.length ? <EmptyState title="Rotas ainda não calculadas" description="Busque os pedidos, atribua aos entregadores e calcule as rotas." /> : null}</section>
    </div>
  )
}
